import { useState } from "react";
import styled from "styled-components";
import { Field, useFormikContext } from "formik";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { getOverrideOptions } from "./utils";
import { cn } from "src/lib/cn";
import { themeVariables } from "src/themeVariables";
import IconButton from "src/components/IconButton";
import { BlockOverrideJoin } from "../../../../../../../src/shared/types/workflows";
import { generateId } from "../../../../../../../src/shared/schemas/CharacterSchema";

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  svg {
    path {
      fill: ${themeVariables.dangerColor};
    }
  }
`;

function Override({ override, blockIndex, selectedOverrideId, onOverrideSelected }) {
  const { values, errors, setFieldValue } = useFormikContext();
  const [isFocused, setIsFocused] = useState(false);
  const [isOptionChangesExpanded, setIsOptionChangesExpanded] = useState(false);
  const overridesPath = `blocks[${blockIndex}].overrides`;

  // Derive editing state from whether this override is selected
  const isEditingOptions = selectedOverrideId === override.id;
  const overrideIndex = values.blocks[blockIndex].overrides.findIndex((o) => o.id === override.id);
  const overrideRootPath = `${overridesPath}[${overrideIndex}]`;

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleToggleEditOptions = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isEditingOptions) {
      onOverrideSelected?.(override.id);
    } else {
      onOverrideSelected?.(null);
    }
  };

  const handleDeleteOverride = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const overrides = values.blocks[blockIndex].overrides.filter((o) => o.id !== override.id);
    setFieldValue(`blocks[${blockIndex}].overrides`, overrides);
  };

  const handleToggleOptionChanges = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOptionChangesExpanded(!isOptionChangesExpanded);
  };

  const handleAddCriterion = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newCriterion = {
      id: generateId(),
      options: {
        variable: "",
        value: "",
        join: BlockOverrideJoin.AND, // Default to AND
      },
    };
    const updatedCriteria = [...override.criteria, newCriterion];
    setFieldValue(`${overrideRootPath}.criteria`, updatedCriteria);
  };

  const handleRemoveCriterion = (criterionIndex) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const updatedCriteria = override.criteria.filter((_, index) => index !== criterionIndex);
    setFieldValue(`${overrideRootPath}.criteria`, updatedCriteria);
  };

  const allOverrideOptions = getOverrideOptions(values.blocks, blockIndex);

  const overrideOptions = allOverrideOptions.reduce((acc, option) => {
    if (!acc[option.group]) {
      acc[option.group] = [];
    }
    acc[option.group].push({
      name: option.name,
      values: option.availableValues,
    });
    // Sort by name within each group
    acc[option.group].sort((a, b) => a.name.localeCompare(b.name));
    return acc;
  }, {});
  return (
    <div
      onClick={() => handleFocus()}
      className={cn(
        "flex flex-row gap-2 justify-between border border-solid rounded-md p-2",
        isEditingOptions ? "border-app-primary-cta border-2 bg-blue-50" : isFocused ? "border-app-primary-cta" : "border-black"
      )}
    >
      <div>
        <div className="flex items-center justify-between gap-2">
          <Field name={`${overrideRootPath}.name`}>
            {({ field }) => (
              <input
                type="text"
                className="border-b border-black flex-1"
                {...field}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            )}
          </Field>
        </div>

        <div>
          {override.criteria.map((criterion, index) => {
            const criterionRootPath = `${overrideRootPath}.criteria[${index}]`;
            const optionsRootPath = `${criterionRootPath}.options`;
            const selectedVariableOption = allOverrideOptions.find((option) => option.name === criterion.options.variable);
            const hasLimitedOptions = selectedVariableOption && selectedVariableOption.availableValues && selectedVariableOption.availableValues.length > 0;
            const isFirstCriterion = index === 0;
            const showJoinOption = !isFirstCriterion;

            return (
              <div
                key={criterion.id}
                className="flex flex-col gap-2 mb-2"
              >
                {showJoinOption && (
                  <div className="flex items-center gap-2">
                    <Field name={`${optionsRootPath}.join`}>
                      {({ field }) => (
                        <select
                          {...field}
                          className="border border-gray-300 rounded px-2 py-1"
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                        >
                          <option value={BlockOverrideJoin.AND}>AND</option>
                          <option value={BlockOverrideJoin.OR}>OR</option>
                        </select>
                      )}
                    </Field>
                  </div>
                )}
                <div className="flex flex-row items-center gap-2">
                  <span>When</span>
                  <Field name={`${optionsRootPath}.variable`}>
                    {({ field }) => (
                      <select
                        {...field}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                      >
                        <option value="">Choose variable</option>
                        {Object.entries(overrideOptions).map(([groupName, options]) => (
                          <optgroup
                            key={groupName}
                            label={groupName}
                          >
                            {options.map((option) => (
                              <option
                                key={option.name}
                                value={option.name}
                              >
                                {option.name}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    )}
                  </Field>
                  <span>is</span>
                  {!hasLimitedOptions && (
                    <Field name={`${optionsRootPath}.value`}>
                      {({ field }) => (
                        <input
                          type="text"
                          {...field}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                        />
                      )}
                    </Field>
                  )}
                  {hasLimitedOptions && (
                    <Field name={`${optionsRootPath}.value`}>
                      {({ field }) => (
                        <select
                          {...field}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                        >
                          <option value="">Select Value</option>
                          {selectedVariableOption.availableValues.map((value) => (
                            <option
                              key={value}
                              value={value}
                            >
                              {value}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                  )}
                  {override.criteria.length > 1 && (
                    <IconButton
                      src="/assets/delete.svg"
                      onClick={handleRemoveCriterion(index)}
                      className="text-red-500"
                    />
                  )}
                </div>
              </div>
            );
          })}
          <div className="mt-2">
            <SudoButton
              variant="tertiary"
              className="mt-0 p-0 text-sm"
              onClick={handleAddCriterion}
            >
              + Add Criterion
            </SudoButton>
          </div>
        </div>

        {override.overrides && override.overrides.length > 0 && (
          <div className="mt-2 bg-gray-50 rounded border">
            <SudoButton
              variant="tertiary"
              className="m-0 p-0 text-sm font-semibold"
              onClick={handleToggleOptionChanges}
            >
              {isOptionChangesExpanded ? "Hide" : "Show"} Option Changes ({override.overrides.length})
            </SudoButton>
            {isOptionChangesExpanded && (
              <div className="space-y-1">
                {override.overrides.map((overrideItem, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-sm"
                  >
                    <span className="font-mono text-gray-600 text-xs">{overrideItem.path.replace("options.", "")}</span>
                    <span className="text-gray-500">→</span>
                    <span className="font-medium text-gray-800">{typeof overrideItem.value === "object" ? JSON.stringify(overrideItem.value) : String(overrideItem.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-row gap-2 items-start">
        <SudoButton
          className="mt-0"
          variant={isEditingOptions ? "primary" : "secondary"}
          size="small"
          onClick={handleToggleEditOptions}
        >
          {isEditingOptions ? "Done Editing" : "Set Options"}
        </SudoButton>
        <ButtonContainer>
          <IconButton
            src="/assets/delete.svg"
            onClick={handleDeleteOverride}
          />
        </ButtonContainer>
      </div>
    </div>
  );
}

export default Override;
